import React from "react";
import UndoIcon from "@material-ui/icons/Undo";
import RedoIcon from "@material-ui/icons/Redo";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import StrikethroughSIcon from "@material-ui/icons/StrikethroughS";
import FormatAlignLeftIcon from "@material-ui/icons/FormatAlignLeft";
import FormatAlignCenterIcon from "@material-ui/icons/FormatAlignCenter";
import FormatAlignRightIcon from "@material-ui/icons/FormatAlignRight";
import FormatAlignJustifyIcon from "@material-ui/icons/FormatAlignJustify";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";
import FormatIndentIncreaseIcon from "@material-ui/icons/FormatIndentIncrease";
import FormatIndentDecreaseIcon from "@material-ui/icons/FormatIndentDecrease";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";
import RemoveIcon from "@material-ui/icons/Remove";
import FormatClearIcon from "@material-ui/icons/FormatClear";
import HorizontalSplitIcon from "@material-ui/icons/HorizontalSplit";
import NoteAddIcon from "@material-ui/icons/NoteAdd";
import LinkButton from "./LinkButton";
import TableGridPicker from "./TableGridPicker";
import { isPageBreak } from "./extensions/PageBreak";

/**
 * The built-in toolbar registry.
 *
 * Names match the TinyMCE toolbar strings already in this codebase exactly
 * (see noticeHtml.js, factsheetHtml.js, OrderDrafting.js, SingleDispute.js),
 * so a call site can pass its existing `toolbar` string unchanged.
 *
 * Each item is either
 *   { title, icon, run(editor), active?(editor), disabled?(editor) }
 * or
 *   { render(editor) }  - supply the element yourself, for anything needing
 *                         its own state (see LinkButton, TableGridPicker).
 *
 * Keyboard shortcuts are not declared here: Toolbar.js finds them in
 * shortcuts.js under the same name and adds them to the tooltip.
 *
 * Not registered here, because they are call-site concerns:
 *   `code`          - TinyMCE's source view (factsheet only); needs a dialog
 *                     and a place to put the edited source.
 *   `ordershortcuts`, `recordaudio` - order drafting; pass via `customItems`.
 */

// Indent/outdent mean list nesting inside a list and margin-left outside it,
// which is what TinyMCE does too.
const inList = (editor) =>
  editor.isActive("bulletList") || editor.isActive("orderedList");

export const BUILT_IN_ITEMS = {
  undo: {
    title: "Undo",
    icon: <UndoIcon fontSize="small" />,
    run: (e) => e.chain().focus().undo().run(),
    disabled: (e) => !e.can().undo(),
  },
  redo: {
    title: "Redo",
    icon: <RedoIcon fontSize="small" />,
    run: (e) => e.chain().focus().redo().run(),
    disabled: (e) => !e.can().redo(),
  },
  bold: {
    title: "Bold",
    icon: <FormatBoldIcon fontSize="small" />,
    run: (e) => e.chain().focus().toggleBold().run(),
    active: (e) => e.isActive("bold"),
  },
  italic: {
    title: "Italic",
    icon: <FormatItalicIcon fontSize="small" />,
    run: (e) => e.chain().focus().toggleItalic().run(),
    active: (e) => e.isActive("italic"),
  },
  underline: {
    title: "Underline",
    icon: <FormatUnderlinedIcon fontSize="small" />,
    run: (e) => e.chain().focus().toggleUnderline().run(),
    active: (e) => e.isActive("underline"),
  },
  strikethrough: {
    title: "Strikethrough",
    icon: <StrikethroughSIcon fontSize="small" />,
    run: (e) => e.chain().focus().toggleStrike().run(),
    active: (e) => e.isActive("strike"),
  },
  alignleft: {
    title: "Align left",
    icon: <FormatAlignLeftIcon fontSize="small" />,
    run: (e) => e.chain().focus().setTextAlign("left").run(),
    active: (e) => e.isActive({ textAlign: "left" }),
  },
  aligncenter: {
    title: "Align centre",
    icon: <FormatAlignCenterIcon fontSize="small" />,
    run: (e) => e.chain().focus().setTextAlign("center").run(),
    active: (e) => e.isActive({ textAlign: "center" }),
  },
  alignright: {
    title: "Align right",
    icon: <FormatAlignRightIcon fontSize="small" />,
    run: (e) => e.chain().focus().setTextAlign("right").run(),
    active: (e) => e.isActive({ textAlign: "right" }),
  },
  alignjustify: {
    title: "Justify",
    icon: <FormatAlignJustifyIcon fontSize="small" />,
    run: (e) => e.chain().focus().setTextAlign("justify").run(),
    active: (e) => e.isActive({ textAlign: "justify" }),
  },
  bullist: {
    title: "Bulleted list",
    icon: <FormatListBulletedIcon fontSize="small" />,
    run: (e) => e.chain().focus().toggleBulletList().run(),
    active: (e) => e.isActive("bulletList"),
  },
  numlist: {
    title: "Numbered list",
    icon: <FormatListNumberedIcon fontSize="small" />,
    run: (e) => e.chain().focus().toggleOrderedList().run(),
    active: (e) => e.isActive("orderedList"),
  },
  indent: {
    title: "Increase indent",
    icon: <FormatIndentIncreaseIcon fontSize="small" />,
    run: (e) =>
      inList(e)
        ? e.chain().focus().sinkListItem("listItem").run()
        : e.chain().focus().indent().run(),
  },
  outdent: {
    title: "Decrease indent",
    icon: <FormatIndentDecreaseIcon fontSize="small" />,
    run: (e) =>
      inList(e)
        ? e.chain().focus().liftListItem("listItem").run()
        : e.chain().focus().outdent().run(),
  },
  blockquote: {
    title: "Blockquote",
    icon: <FormatQuoteIcon fontSize="small" />,
    run: (e) => e.chain().focus().toggleBlockquote().run(),
    active: (e) => e.isActive("blockquote"),
  },
  hr: {
    title: "Horizontal rule",
    icon: <RemoveIcon fontSize="small" />,
    run: (e) => e.chain().focus().setHorizontalRule().run(),
  },
  removeformat: {
    title: "Clear formatting",
    icon: <FormatClearIcon fontSize="small" />,
    // Marks only. TinyMCE's removeformat strips inline formatting and leaves
    // block structure alone; adding clearNodes() here would dissolve the
    // surrounding table or list, which on an order sheet is destructive.
    run: (e) => e.chain().focus().unsetAllMarks().run(),
  },
  link: {
    title: "Link",
    render: (editor) => <LinkButton editor={editor} />,
  },
  table: {
    title: "Table",
    render: (editor) => <TableGridPicker editor={editor} />,
  },
  pagebreak: {
    // Toolbar.js appends the shortcut from shortcuts.js.
    title:
      "Page break - continue on the next page under the case number and page number, or remove this one",
    icon: <HorizontalSplitIcon fontSize="small" />,
    // Lit while the caret sits on a break; clicking then removes it.
    active: (e) => isPageBreak(e),
    run: (e) =>
      isPageBreak(e)
        ? e.chain().focus().removePageBreak().run()
        : e.chain().focus().insertPageBreak().run(),
  },
  newpage: {
    title: "New page - continue on the next page under the case number and page number",
    icon: <NoteAddIcon fontSize="small" />,
    run: (e) => e.chain().focus().newPage().run(),
  },
};

export default BUILT_IN_ITEMS;
